import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, Modal, Row } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrosshairs } from '@fortawesome/free-solid-svg-icons/faCrosshairs';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons/faLocationDot';
import { CheckOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import AddressStatusType from "../enums/AddressStatusType";
import GoogleMapLocationSelection from './GoogleMapLocationSelection';
import { nonEmpty, showErrorNotification } from '../../../swzutils';
import CountrySelect from './CountrySelect';
import StateSelect from './StateSelect';
import HelpLabel from "./HelpLabel";
import LatLngModal from "./LatLngModal";
import GooglePlacesAutoComplete from "./GooglePlacesAutoComplete";
import { MELBOURNE_CBD } from "./googleMap/Model";
import { validateEmailCsv } from "../../../swzutils/ValidationUtil";
const FindAddressModal = ({ open, onCancel, onAddressSelected }) => {
    return (_jsxs(Modal, { title: "Address Search", open: open, onCancel: onCancel, destroyOnHidden: true, footer: [
            _jsx(Button, { onClick: onCancel, children: "Cancel" }, "cancel"),
        ], children: [_jsx("p", { children: "Enter search text (address, company name etc..)" }), _jsx("div", { children: _jsx(GooglePlacesAutoComplete, { autoFocus: true, showUnitOrFlat: false, onChange: onAddressSelected }) })] }));
};
const AddressPanel = ({ form, title = 'Address', showStatus = false, overwriteAddressOnGeolocate = true, showCompanyContactDetails = false, readOnly = false, editContactsOnly = false, showMapAlways = false, contactNameRequired = false, contactPhoneRequired = false, waypoint, onLocationChanged, onSiteRadiusChanged, onSaveAsSenderReceiverClicked }) => {
    const [mapCentre, setMapCentre] = useState(MELBOURNE_CBD);
    const [addressSelectionVisible, setAddressSelectionVisible] = useState(false);
    const [addressSelections, setAddressSelections] = useState([]);
    const [geocodingInProgress, setGeocodingInProgress] = useState(false);
    const [findAddressModalVisible, setFindAddressModalVisible] = useState(false);
    const [latLngModalVisible, setLatLngModalVisible] = useState(false);
    const [addressStatus, setAddressStatus] = useState(AddressStatusType.INVALID);
    useEffect(() => {
        if (waypoint?.address?.latitude && waypoint?.address?.longitude) {
            setMapCentre({
                lat: waypoint.address.latitude,
                lng: waypoint.address.longitude
            });
            setAddressStatus(AddressStatusType.VALIDATED);
        }
    }, [waypoint]);
    const onLocationCentreChanged = (centre) => {
        setAddressStatus(AddressStatusType.VALIDATED);
        // The map circle is a controlled component, so the centre must be fed back or it snaps back after a drag.
        setMapCentre(centre);
        onLocationChanged && onLocationChanged(centre);
    };
    const onFindAddressClicked = () => {
        setFindAddressModalVisible(true);
    };
    const onLatLngClicked = () => {
        setLatLngModalVisible(true);
    };
    const onLatLngEntered = (lat, lng) => {
        const addressLatLng = { lat, lng };
        onLocationCentreChanged(addressLatLng);
        setMapCentre(addressLatLng);
        setLatLngModalVisible(false);
    };
    const geocodeThisAddress = () => {
        queryAddressUsingPlacesApi(buildFormattedAddress());
    };
    const queryAddressUsingPlacesApi = (formattedAddress) => {
        setGeocodingInProgress(true);
        //console.log("queryAddressUsingPlacesApi:", address);
        const autocompleteService = new google.maps.places.AutocompleteService();
        autocompleteService.getQueryPredictions({ input: formattedAddress }, (results, status) => {
            //console.log("results:", results);
            //console.log("status:", status);
            setGeocodingInProgress(false);
            if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
                //console.log("results.length:", results.length);
                if (results.length === 1 && results[0].place_id) {
                    applyGooglePlace(results[0].place_id);
                }
                else {
                    setAddressSelections(results);
                    setAddressSelectionVisible(true);
                }
            }
            else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                showErrorNotification('No such address');
            }
            else {
                showErrorNotification("Address search was not successful for the following reason:" + status);
            }
        });
    };
    const applyGooglePlace = (placeId) => {
        setGeocodingInProgress(true);
        //console.log("applyGooglePlace:", placeId);
        const request = {
            placeId: placeId,
            fields: ['name', 'types', 'address_components', 'geometry']
        };
        const placesService = new google.maps.places.PlacesService(document.createElement("div"));
        placesService.getDetails(request, (place, status) => {
            //console.log("place:", place);
            //console.log("status:", status);
            setGeocodingInProgress(false);
            if (place && status === google.maps.places.PlacesServiceStatus.OK) {
                applyGoogleAddress(place);
            }
            else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                showErrorNotification('No such address');
            }
            else {
                showErrorNotification("Address geocode was not successful for the following reason:" + status);
            }
        });
    };
    const buildFormattedAddress = () => {
        const unitOrFlat = form.getFieldValue('unitOrFlat');
        const street = form.getFieldValue('street');
        const suburb = form.getFieldValue('suburb');
        const postcode = form.getFieldValue('postcode');
        const state = form.getFieldValue('state');
        const country = form.getFieldValue('countryCode') === "NZ" ? "New Zealand" : "Australia";
        // 1 Williamstown Rd, Port Melbourne VIC 3207, Australia
        return nonEmpty `${unitOrFlat} ${street} ${suburb} ${state} ${postcode} ${country}`;
    };
    const selectAddress = (index) => {
        if (addressSelections[index].place_id) {
            applyGooglePlace(addressSelections[index].place_id);
        }
    };
    const onModalAddressSelected = (value, companyName) => {
        if (!readOnly && overwriteAddressOnGeolocate) {
            //console.log("onModalAddressSelected", address, companyName);
            let fieldValues = {
                ...value
            };
            if (companyName) {
                fieldValues['companyName'] = companyName;
            }
            form.setFieldsValue(fieldValues);
        }
        if (value && value.latitude && value.longitude) {
            const addressLatLng = { lat: value.latitude, lng: value.longitude };
            onLocationCentreChanged && onLocationCentreChanged(addressLatLng);
            setMapCentre(addressLatLng);
        }
        hideAddressSelectionModal();
    };
    const applyGoogleAddress = (googleAddress) => {
        if (!readOnly && overwriteAddressOnGeolocate) {
            let fieldValues = {};
            //console.log(googleAddress);
            fieldValues['street'] = getAddressComponentShortName(googleAddress.address_components, "street_number")
                + " " + getAddressComponentShortName(googleAddress.address_components, "route");
            fieldValues['suburb'] = getAddressComponentShortName(googleAddress.address_components, "locality");
            fieldValues['postcode'] = getAddressComponentShortName(googleAddress.address_components, "postal_code");
            fieldValues['state'] = getAddressComponentShortName(googleAddress.address_components, "administrative_area_level_1");
            fieldValues['countryCode'] = getAddressComponentShortName(googleAddress.address_components, "country");
            if (googleAddress.types && googleAddress.name && googleAddress.types.includes("store")) {
                fieldValues['companyName'] = googleAddress.name;
            }
            form.setFieldsValue(fieldValues);
        }
        if (googleAddress.geometry && googleAddress.geometry.location) {
            const addressLatLng = {
                lat: googleAddress.geometry.location.lat(),
                lng: googleAddress.geometry.location.lng()
            };
            onLocationCentreChanged && onLocationCentreChanged(addressLatLng);
            setMapCentre(addressLatLng);
        }
        hideAddressSelectionModal();
    };
    const getAddressComponentShortName = (addressComponents, typeName) => {
        for (let addrComponent of addressComponents) {
            if (addrComponent.types) {
                if (addrComponent.types.includes(typeName)) {
                    return addrComponent.short_name;
                }
            }
        }
        return "";
    };
    const hideAddressSelectionModal = () => {
        setAddressSelectionVisible(false);
        setAddressSelections([]);
        setGeocodingInProgress(false);
        setFindAddressModalVisible(false);
    };
    const validateAndNotifySaveAsSendReceiverButtonClicked = () => {
        if (addressStatus !== AddressStatusType.VALIDATED) {
            showErrorNotification("Please position centre of the circle to the location for this address");
            return;
        }
        onSaveAsSenderReceiverClicked();
    };
    return (_jsxs("div", { className: "address-panel", children: [_jsxs(Card, { title: _jsxs(_Fragment, { children: [title, "\u00A0\u00A0\u00A0", showStatus ? addressStatus?.renderTag() : null] }), extra: !readOnly &&
                    _jsxs(_Fragment, { children: [_jsxs(Button, { onClick: onFindAddressClicked, loading: geocodingInProgress, children: [_jsx(SearchOutlined, {}), "\u00A0Address Search"] }), _jsx(HelpLabel, { title: "", helpText: "Using this button you can search for an address to quickly populate it in the fields below." }), onSaveAsSenderReceiverClicked &&
                                _jsxs(Button, { onClick: validateAndNotifySaveAsSendReceiverButtonClicked, className: "pull-right", children: [_jsx(SaveOutlined, {}), "\u00A0 Save as Saved Location"] })] }), type: "inner", variant: 'borderless', children: [showCompanyContactDetails &&
                        _jsxs(Row, { gutter: 5, children: [_jsx(Col, { sm: 12, lg: 6, children: _jsx(Form.Item, { name: "companyName", label: "Company Name", initialValue: waypoint?.companyName, rules: [
                                            { max: 100, message: 'Please limit the company name to 100 characters.' }
                                        ], children: _jsx(Input, { readOnly: readOnly && !editContactsOnly }) }) }), _jsx(Col, { sm: 12, lg: 6, children: _jsx(Form.Item, { name: "contactName", label: "Contact Name", initialValue: waypoint?.contactName, rules: [
                                            { max: 100, message: 'Please limit the contact name to 100 characters.' },
                                            { required: contactNameRequired, message: 'Contact Name is mandatory' }
                                        ], children: _jsx(Input, { readOnly: readOnly && !editContactsOnly }) }) }), _jsx(Col, { sm: 12, lg: 6, children: _jsx(Form.Item, { name: "contactTelephone", label: "Contact Phone", initialValue: waypoint?.contactTelephone, rules: [
                                            { max: 20, message: 'Please limit the contact phone number to 20 characters.' },
                                            { required: contactPhoneRequired, message: 'Contact Phone is mandatory' }
                                        ], children: _jsx(Input, { readOnly: readOnly && !editContactsOnly }) }) }), _jsx(Col, { sm: 12, lg: 6, children: _jsx(Form.Item, { name: "contactEmail", label: "Contact Email", initialValue: waypoint?.contactEmail, rules: [{
                                                max: 500, message: 'Please limit to 500 characters.'
                                            }, {
                                                validator: validateEmailCsv
                                            }], children: _jsx(Input, { readOnly: readOnly && !editContactsOnly }) }) })] }), _jsxs(Row, { gutter: 5, children: [_jsx(Col, { sm: 8, children: _jsx(Form.Item, { name: "unitOrFlat", label: "Prefix", initialValue: waypoint?.address?.unitOrFlat, rules: [
                                        { max: 50, message: 'Please limit the prefix to 50 characters.' }
                                    ], children: _jsx(Input, { placeholder: "Unit, Flat, Shop or Level", readOnly: readOnly }) }) }), _jsx(Col, { sm: 16, children: _jsx(Form.Item, { name: "street", label: "Street", initialValue: waypoint?.address?.street, rules: [
                                        { required: !readOnly, message: 'Please enter a street number and name.' },
                                        { max: 256, message: 'Please limit the street to 256 characters.' }
                                    ], children: _jsx(Input, { placeholder: "e.g. 25 Flinders Street", readOnly: readOnly }) }) })] }), _jsxs(Row, { gutter: 5, children: [_jsx(Col, { sm: 8, children: _jsx(Form.Item, { name: "suburb", label: "Suburb", initialValue: waypoint?.address?.suburb, rules: [
                                        { required: !readOnly, message: 'Please enter a suburb.' },
                                        { max: 128, message: 'Please limit the suburb to 128 characters.' }
                                    ], children: _jsx(Input, { placeholder: "e.g. Melbourne", readOnly: readOnly }) }) }), _jsx(Col, { sm: 4, children: _jsx(Form.Item, { name: "state", label: "State", initialValue: waypoint?.address?.state, rules: [
                                        { required: !readOnly, message: 'Please select a state.' }
                                    ], children: _jsx(StateSelect, { disabled: readOnly }) }) }), _jsx(Col, { sm: 4, children: _jsx(Form.Item, { name: "postcode", label: "Postcode", initialValue: waypoint?.address?.postcode, rules: [
                                        { required: !readOnly, message: 'Please enter a postcode.' },
                                        { max: 128, message: 'Please limit the postcode to 128 characters.' }
                                    ], children: _jsx(Input, { placeholder: "e.g. 3000", readOnly: readOnly }) }) }), _jsx(Col, { sm: 8, children: _jsx(Form.Item, { name: "countryCode", label: "Country", initialValue: waypoint?.address?.countryCode, rules: [
                                        { required: !readOnly, message: 'Please select a country.' }
                                    ], children: _jsx(CountrySelect, { disabled: readOnly }) }) })] })] }), showMapAlways &&
                _jsx(Card, { title: `${title} Map`, extra: !readOnly &&
                        _jsxs(_Fragment, { children: [_jsxs(Button, { onClick: geocodeThisAddress, loading: geocodingInProgress, children: [_jsx(FontAwesomeIcon, { icon: faCrosshairs }), "\u00A0Reposition map to address"] }), _jsx(HelpLabel, { title: "", helpText: "Here you can re position the address and select the exact match." }), "\u00A0\u00A0\u00A0", _jsxs(Button, { onClick: onLatLngClicked, children: [_jsx(FontAwesomeIcon, { icon: faLocationDot }), "\u00A0Enter Lat/Lng"] }), _jsx(HelpLabel, { title: "", helpText: "If you know the latitude and longitude of this address you can enter it directly here." })] }), type: "inner", variant: 'borderless', children: _jsx(GoogleMapLocationSelection, { centre: mapCentre, siteRadius: waypoint?.address ? waypoint?.address.siteRadius : 200, zoom: 14, editable: !readOnly, onCentreChanged: onLocationCentreChanged, onSiteRadiusChanged: onSiteRadiusChanged }) }), _jsxs(Modal, { title: "Select Address", open: addressSelectionVisible, onCancel: hideAddressSelectionModal, footer: [
                    _jsx(Button, { onClick: hideAddressSelectionModal, children: "Cancel" }, "back"),
                ], children: [_jsx("p", { children: "Address search results" }), _jsxs("table", { className: "table full-width table-hover", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Address" }), _jsx("th", { className: "text-center", children: "Select" })] }) }), _jsx("tbody", { children: addressSelections.map((addressResult, index) => (_jsxs("tr", { children: [_jsx("td", { children: addressResult.description }), _jsx("td", { className: "text-center", children: _jsx(Button, { onClick: () => selectAddress(index), children: _jsx(CheckOutlined, {}) }) })] }, index))) })] })] }), _jsx(FindAddressModal, { open: findAddressModalVisible, onCancel: () => setFindAddressModalVisible(false), onAddressSelected: onModalAddressSelected }), _jsx(LatLngModal, { open: latLngModalVisible, onCancel: () => setLatLngModalVisible(false), onSave: onLatLngEntered })] }));
};
export default AddressPanel;
